import {
	createContext,
	type ReactElement,
	type ReactNode,
	useContext,
	useMemo,
} from "react";
import type { MapAdapter } from "@/features/map/types";

export interface MapContextValue {
	orgId: string;
	accessToken: string;
	adapter: MapAdapter;
}

const MapContext = createContext<MapContextValue | null>(null);

export interface MapProviderProps {
	orgId: string;
	accessToken: string;
	adapter: MapAdapter;
	children: ReactNode;
}

/**
 * Injects the active entity map adapter for the workspace panel.
 */
export function MapProvider({
	orgId,
	accessToken,
	adapter,
	children,
}: MapProviderProps): ReactElement {
	const value = useMemo(
		() => ({ orgId, accessToken, adapter }),
		[accessToken, adapter, orgId],
	);

	return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
}

/**
 * Reads the map provider. Throws when used outside {@link MapProvider}.
 */
export function useMapContext(): MapContextValue {
	const value = useContext(MapContext);
	if (value == null) {
		throw new Error("useMapContext must be used within MapProvider");
	}
	return value;
}
